import { defineComponent } from "vue";
import { Vue3Marquee } from "vue3-marquee";

import { PAGE_NAME } from "./attributes";
import { currencyPairs, CurrencyPair } from "@/data/wallet/pairs";

import {
  WalletMain,
  WalletHistory,
  WalletApplications,
  WalletDeposit,
  WalletWithdrawal,
  WalletRequisitesInfo,
  WalletVerification,
  WalletVerificationRequisites,
  WalletCriptoDepositAddress,
  WalletCriptoWithdrawal,
  WalletVerificationWithdrawal,
} from "@/components/pages/wallet";
import {
  SharedBlockCurrencySelect,
  SharedBlockCurrencyPair,
} from "@/components/shared";
import { AdminApplicationType } from "@/shared/repository/modules/admin/application/enums";
import { DepositWithdrawalHistoryShortResponse } from "@/shared/repository/modules/deposit-withdrawal/repo";
import { ApplicationHistoryShortResponse } from "@/shared/repository/modules/application/repo";
import { WalletModalFactory } from "@/shared/repository/modules/wallet/factory";
import { getCookie, setCookie } from "@/shared/utils/cookie-helpers";
import { animateValue } from "@/shared/utils/animation-helpers";

export type ModalComponent =
  | typeof WalletDeposit
  | typeof WalletWithdrawal
  | typeof WalletRequisitesInfo
  | typeof WalletVerification
  | typeof WalletVerificationRequisites
  | typeof WalletCriptoDepositAddress
  | typeof WalletCriptoWithdrawal
  | typeof WalletVerificationWithdrawal
  | typeof WalletMain
  | "";

export type TickerPair = {
  currency_id_from: number;
  currency_id_to: number;
  currency_type_from: "fiat" | "crypto";
  currency_type_to: "fiat" | "crypto";
};

const isSamePair = (pair1: TickerPair, pair2: TickerPair): boolean =>
  pair1.currency_id_from === pair2.currency_id_from &&
  pair1.currency_id_to === pair2.currency_id_to;

const addPairIfMissing = (pairs: TickerPair[], pairToAdd: TickerPair): void => {
  if (!pairs.some((pair) => isSamePair(pair, pairToAdd))) {
    pairs.push(pairToAdd);
  }
};

export default defineComponent({
  name: PAGE_NAME,
  components: {
    WalletMain,
    WalletHistory,
    WalletApplications,
    WalletDeposit,
    WalletWithdrawal,
    WalletRequisitesInfo,
    WalletVerification,
    WalletCriptoDepositAddress,
    WalletCriptoWithdrawal,
    WalletVerificationWithdrawal,
    SharedBlockCurrencySelect,
    SharedBlockCurrencyPair,
    Vue3Marquee,
  },
  data() {
    return {
      depositWithdrawalHistory: <DepositWithdrawalHistoryShortResponse[]>[],
      applicationHistory: <ApplicationHistoryShortResponse[]>[],
      walletRepo: this.$projectServices.walletRepo,
      depositWithdrawalRepo: this.$projectServices.depositWithdrawalRepo,
      depositWithdrawalRepository:
        this.$projectServices.depositWithdrawalRepository,
      applicationRepository: this.$projectServices.applicationRepository,
      walletRepository: this.$projectServices.walletRepository,

      pairs: currencyPairs,
      ratesMap: {} as Record<string, { buy: number; sell: number }>,
      shouldAnimate: false,
      tickerInterval: null as ReturnType<typeof setInterval> | null,
      resizeObserver: null as ResizeObserver | null,
      isInitialTickerLoad: true,
      selectedPair: null as TickerPair | null,
      heroCurrency: getCookie("balance_fiat_currency") || "USD",
      heroCurrencies: ["USD", "RUB", "BTC", "USDT"],
      isHeroCurrencyLoading: false,
      animatedBalance: 0,
      balanceRafId: null as number | null,
      heroMx: "50%",
      heroMy: "50%",
    };
  },
  async created(): Promise<void> {
    await this.loadData();
  },
  async mounted(): Promise<void> {
    this.setupResizeObserver();
    window.addEventListener("resize", this.checkIfAnimationNeeded);

    this.tickerInterval = setInterval(() => {
      this.loadTicker();
    }, 60000);
  },
  beforeUnmount(): void {
    window.removeEventListener("resize", this.checkIfAnimationNeeded);
    this.depositWithdrawalRepo.UPDATE_DEPOSIT({
      id: 0,
      currency_id: 0,
      amount: 0,
      currencyName: "",
    });
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.tickerInterval) {
      clearInterval(this.tickerInterval);
    }
    if (this.balanceRafId !== null) {
      cancelAnimationFrame(this.balanceRafId);
    }
  },
  watch: {
    "walletRepo.list.length": {
      flush: "post",
      handler() {
        this.setupResizeObserver();
      },
    },
    rawBalanceValue: {
      immediate: true,
      handler(newValue: number, oldValue: number | undefined): void {
        this.animateBalance(oldValue ?? 0, newValue);
      },
    },
  },
  computed: {
    currencyMap(): Record<number, string> {
      const map: Record<number, string> = {};
      this.walletRepo.list.forEach((currency) => {
        map[currency.currency_id] = currency.name;
      });
      return map;
    },

    pairsWithRates(): Array<
      CurrencyPair & { buyRate: number; sellRate: number }
    > {
      return this.pairs.map((pair: CurrencyPair) => {
        const key = `${pair.currency_id_from}-${pair.currency_id_to}`;
        const rates = this.ratesMap[key] || { buy: 0, sell: 0 };
        return {
          ...pair,
          buyRate: rates.buy,
          sellRate: rates.sell,
        };
      });
    },

    selectedPairBuyRate(): number {
      if (!this.selectedPair) return 0;

      const key = `${this.selectedPair.currency_id_from}-${this.selectedPair.currency_id_to}`;
      return this.ratesMap[key]?.buy || 0;
    },

    selectedPairSellRate(): number {
      if (!this.selectedPair) return 0;
      const key = `${this.selectedPair.currency_id_from}-${this.selectedPair.currency_id_to}`;
      return this.ratesMap[key]?.sell || 0;
    },

    displayedModal(): ModalComponent {
      switch (this.walletRepo.modal.type) {
        case AdminApplicationType.deposit:
          return WalletDeposit;
        case AdminApplicationType.withdrawal:
          return WalletWithdrawal;
        case AdminApplicationType.depositRequisites:
          return WalletRequisitesInfo;
        case AdminApplicationType.verificationDeposit:
        case AdminApplicationType.verificationWithdrawal:
          return WalletVerification;
        case AdminApplicationType.verificationWithdrawalRequisites:
          return WalletVerificationRequisites;
        case AdminApplicationType.criptoDepositAddress:
          return WalletCriptoDepositAddress;
        case AdminApplicationType.walletWithdrawal:
          return WalletCriptoWithdrawal;
        case AdminApplicationType.verificationWalletWithdrawal:
          return WalletVerificationWithdrawal;
        case AdminApplicationType.verificationBankRequisites:
          return WalletVerificationRequisites;
        default:
          return "";
      }
    },

    isModalShow(): boolean {
      return !!this.walletRepo.modal.type;
    },

    displayedFiatCurrency(): string {
      return Object.keys(this.walletRepo.balance.fiat)[0] || "USD";
    },

    displayedFiatSymbol(): string {
      const symbols: Record<string, string> = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        RUB: "₽",
        KZT: "₸",
        CNY: "¥",
        BTC: "₿",
        USDT: "₮",
      };

      return symbols[this.heroCurrency] || "";
    },

    rawBalanceValue(): number {
      const isCrypto = ["BTC", "USDT"].includes(this.heroCurrency);
      const balances = (
        isCrypto ? this.walletRepo.balance.crypto : this.walletRepo.balance.fiat
      ) as Record<string, number>;

      return balances[this.heroCurrency] ?? Object.values(balances)[0] ?? 0;
    },

    displayedFiatBalance(): string {
      const isCrypto = ["BTC", "USDT"].includes(this.heroCurrency);

      return Number(this.animatedBalance).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: isCrypto ? 8 : 2,
      });
    },

    heroSpotStyle(): Record<string, string> {
      return { "--mx": this.heroMx, "--my": this.heroMy };
    },

    displayedBalanceInteger(): string {
      return this.displayedFiatBalance.split(".")[0];
    },

    displayedBalanceFraction(): string {
      const fraction = this.displayedFiatBalance.split(".")[1];
      return fraction ? `.${fraction}` : "";
    },

    hasWalletBalance(): boolean {
      return this.walletRepo.list.some(
        (item) => !item.frozen && !item.currency?.frozen && item.amount > 0
      );
    },
  },
  methods: {
    async handleHeroCurrency(currency: string): Promise<void> {
      if (currency === this.heroCurrency || this.isHeroCurrencyLoading) return;

      this.heroCurrency = currency;
      this.isHeroCurrencyLoading = true;
      setCookie("wallet_hero_currency", currency);

      try {
        const isCrypto = ["BTC", "USDT"].includes(currency);
        await this.walletRepo.wallet({
          fiat: isCrypto ? this.displayedFiatCurrency : currency,
          crypto: isCrypto
            ? currency
            : Object.keys(this.walletRepo.balance.crypto)[0] || "USDT",
        });
      } finally {
        this.isHeroCurrencyLoading = false;
      }
    },

    animateBalance(from: number, to: number): void {
      if (this.balanceRafId !== null) cancelAnimationFrame(this.balanceRafId);

      this.balanceRafId = animateValue(from, to, (value) => {
        this.animatedBalance = value;
      });
    },

    handleHeroPointerMove(event: PointerEvent): void {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.heroMx = `${event.clientX - rect.left}px`;
      this.heroMy = `${event.clientY - rect.top}px`;
    },

    handleDeposit(): void {
      this.walletRepo.UPDATE_MODAL(
        WalletModalFactory({ type: AdminApplicationType.deposit })
      );
    },

    handleWithdrawal(): void {
      this.walletRepo.UPDATE_MODAL(
        WalletModalFactory({ type: AdminApplicationType.withdrawal })
      );
    },

    async loadData() {
      try {
        const [depositWithdrawalHistory, applicationHistory] =
          await Promise.all([
            this.depositWithdrawalRepository.historyShort(),
            this.applicationRepository.historyShort(),
          ]);
        this.depositWithdrawalHistory = depositWithdrawalHistory;
        this.applicationHistory = applicationHistory;
      } catch (error) {
        console.log(error);
      }
    },

    closeModal() {
      this.walletRepo.modal.type = "";
    },

    async loadTicker(singlePair?: TickerPair) {
      try {
        const pairsToLoad: TickerPair[] = singlePair
          ? [singlePair]
          : this.pairs.map((pair) => ({
              currency_id_from: pair.currency_id_from,
              currency_id_to: pair.currency_id_to,
              currency_type_from: pair.currency_type_from,
              currency_type_to: pair.currency_type_to,
            }));

        if (this.selectedPair) {
          addPairIfMissing(pairsToLoad, this.selectedPair);
        }

        const res = await this.walletRepository.ticker({ pairs: pairsToLoad });

        const ratesMap: Record<string, { buy: number; sell: number }> =
          singlePair ? { ...this.ratesMap } : {};

        res.quotes.forEach((quote) => {
          const key = `${quote.pair.currency_id_from}-${quote.pair.currency_id_to}`;
          ratesMap[key] = {
            buy: quote.buy_price,
            sell: quote.sell_price,
          };
        });

        this.ratesMap = ratesMap;
        await this.$nextTick();
        this.checkIfAnimationNeeded();
      } catch (error) {
        console.error("Failed to load ticker:", error);
      }
    },

    handlePairChanged(pair: TickerPair) {
      this.selectedPair = pair;

      if (this.isInitialTickerLoad) {
        // При первой загрузке загружаем все пары
        this.isInitialTickerLoad = false;
        this.loadTicker();
      } else {
        // При последующих изменениях загружаем только выбранную пару
        this.loadTicker(pair);
      }
    },

    setupResizeObserver() {
      this.$nextTick(() => {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        const pairsContainer = this.$el.querySelector(
          ".wallet-page__pairs"
        ) as HTMLElement;
        const marqueeContent = this.$el.querySelector(
          ".vue3-marquee .marquee"
        ) as HTMLElement;

        if (pairsContainer && marqueeContent) {
          this.resizeObserver = new ResizeObserver(() => {
            this.checkIfAnimationNeeded();
          });

          this.resizeObserver.observe(pairsContainer);
          this.resizeObserver.observe(marqueeContent);

          this.checkIfAnimationNeeded();
        } else {
          this.shouldAnimate = false;
        }
      });
    },

    checkIfAnimationNeeded() {
      const pairsContainer = this.$el.querySelector(
        ".wallet-page__pairs"
      ) as HTMLElement;
      const marqueeContent = this.$el.querySelector(
        ".vue3-marquee .marquee"
      ) as HTMLElement;

      if (pairsContainer && marqueeContent) {
        const containerWidth = pairsContainer.offsetWidth;
        const contentWidth = marqueeContent.scrollWidth;

        this.shouldAnimate = contentWidth > containerWidth;
      }
    },
  },
});
